import { useEffect, useState } from 'react';
import { Plus, Trash2, Wrench } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';

type Kind = 'stdio' | 'http' | 'sse';

interface Mcp {
  id: string;
  name: string;
  description: string;
  kind: Kind;
  config: Record<string, unknown>;
  updatedAt?: string | null;
}

export function McpsView() {
  const [items, setItems] = useState<Mcp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Mcp | 'new' | null>(null);

  const reload = async () => {
    try {
      setItems(await api.get<Mcp[]>('/api/mcps'));
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title="MCPs"
        description="Reusable MCP server configs that agents can attach to a session."
        action={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus />
            New
          </Button>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && <ItemGroupEmpty>no MCPs yet</ItemGroupEmpty>}

        {items.map((m) => (
          <Item key={m.id} interactive onClick={() => setEditing(m)}>
            <ItemMedia>
              <Wrench />
            </ItemMedia>
            <ItemContent>
              <ItemHeader>
                <ItemTitle>{m.name}</ItemTitle>
                <Badge variant="secondary" className="text-[10px]">{m.kind}</Badge>
              </ItemHeader>
              {m.description && <ItemDescription>{m.description}</ItemDescription>}
            </ItemContent>
            <ItemActions>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (!(await showConfirm(`Delete MCP "${m.name}"?`))) return;
                  try {
                    await api.del(`/api/mcps/${m.id}`);
                    void reload();
                  } catch (e) {
                    void showAlert((e as { message: string }).message);
                  }
                }}
              >
                <Trash2 />
              </Button>
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>

      <McpEditorDialog
        open={editing !== null}
        initial={editing === 'new' ? null : (editing as Mcp | null)}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void reload();
        }}
      />
    </ItemGroup>
  );
}

function McpEditorDialog({
  open,
  initial,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  initial: Mcp | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [kind, setKind] = useState<Kind>(initial?.kind ?? 'stdio');
  const [configText, setConfigText] = useState(
    initial ? JSON.stringify(initial.config, null, 2) : defaultConfigFor('stdio'),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setKind(initial?.kind ?? 'stdio');
    setConfigText(initial ? JSON.stringify(initial.config, null, 2) : defaultConfigFor('stdio'));
    setErr(null);
  }, [open, initial]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const config = JSON.parse(configText);
      const body = { name, description, kind, config };
      if (initial) await api.patch(`/api/mcps/${initial.id}`, body);
      else await api.post('/api/mcps', body);
      onSaved();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{initial ? `Edit "${initial.name}"` : 'New MCP'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="mcp-name">Name</Label>
            <Input id="mcp-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mcp-desc">Description</Label>
            <Input id="mcp-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Kind</Label>
            <Select
              value={kind}
              onValueChange={(v) => {
                setKind(v as Kind);
                if (!initial) setConfigText(defaultConfigFor(v as Kind));
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stdio">stdio</SelectItem>
                <SelectItem value="http">http</SelectItem>
                <SelectItem value="sse">sse</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mcp-cfg">Config (JSON)</Label>
            <Textarea
              id="mcp-cfg"
              value={configText}
              onChange={(e) => setConfigText(e.target.value)}
              className="min-h-[160px] font-mono text-xs"
            />
          </div>
          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !name} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultConfigFor(kind: Kind): string {
  if (kind === 'stdio') {
    return JSON.stringify({ command: 'npx', args: ['-y', 'some-mcp'], env: {} }, null, 2);
  }
  return JSON.stringify({ url: 'https://example.com/mcp', headers: {} }, null, 2);
}
